import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAt,
  endAt,
  limit,
  where,
  writeBatch,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  PLAN_FREE,
  PLAN_PREMIUM,
  PREMIUM_VIGENCIA_DIAS_DEFAULT,
} from '../../../core/monetizacion/planes.js';
import {
  COL_PROFESIONALES_SERVICIOS,
  SUB_TARJETAS,
} from '../../servicios/data/serviciosRepository.js';

const PUBLICIDAD_HOME_DOC = 'activa';
const RX_UID = /^[A-Za-z0-9_-]{10,128}$/;

export function createAdminRepository({ getFirebase }) {
  async function refreshAdminAuthToken() {
    const u = getFirebase()?.auth?.currentUser;
    if (u) await u.getIdToken(true);
  }
  function tipoDesdeDoc(u) {
    if (u?.negocioPremium === true) return 'marketPremium';
    if (String(u?.plan || '').toLowerCase() === PLAN_PREMIUM) return 'userPremium';
    return 'free';
  }

  /**
   * Construye una fila de cuenta a partir del doc `usuarios/{uid}`.
   * Mantiene los alias `personaId` y `principalUid` para compat con el presenter,
   * que históricamente trabajaba con persona canónica.
   */
  function filaCuentaDesdeUsuarioDoc(uDoc) {
    const u = uDoc.data() || {};
    const uid = uDoc.id;
    const estado = String(u.estadoCuenta || '').trim() || 'activa';
    return {
      id: uid,
      uid,
      personaId: uid,
      principalUid: uid,
      uids: [uid],
      nombre: String(u.nombre || u.nombre_publico || '').trim() || uid,
      dni: '—',
      email: String(u.email || '').trim(),
      user_tag: String(u.user_tag || '').trim().toUpperCase(),
      telefono: String(u.telefono || '').trim(),
      tipoCuenta: tipoDesdeDoc(u),
      premium_hasta_principal: u.premium_hasta ?? null,
      comision_deuda_ars: Number(u.comision_deuda_ars) || 0,
      saldo_favor_ars: Number(u.saldo_favor_ars) || 0,
      comision_ventas_impagas_count: Number(u.comision_ventas_impagas_count) || 0,
      estadoCuentaPrincipal: estado,
      estadoModeracionPersona: estado,
    };
  }

  return {
    async listarUsuarios() {
      const { db } = getFirebase();
      const usuariosQ = query(
        collection(db, 'usuarios'),
        orderBy('actualizadoEn', 'desc'),
        limit(200)
      );
      const snap = await getDocs(usuariosQ);
      return snap.docs.map((d) => filaCuentaDesdeUsuarioDoc(d));
    },

    /**
     * Busca usuarios por prefijo de email (lo más práctico tras el wipe).
     * Mantiene el nombre legacy para no romper el presenter.
     */
    async buscarPersonasPorPrefijoDni(prefix) {
      const raw = String(prefix || '').trim().toLowerCase();
      if (!raw || raw.length < 2) return [];
      const { db } = getFirebase();
      const q = query(
        collection(db, 'usuarios'),
        orderBy('email'),
        startAt(raw),
        endAt(`${raw}\uf8ff`),
        limit(20)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => filaCuentaDesdeUsuarioDoc(d));
    },

    async obtenerCuentaPorPersonaId(uidOrPersonaId) {
      const { db } = getFirebase();
      const uid = String(uidOrPersonaId || '').trim();
      if (!RX_UID.test(uid)) return null;
      const uSnap = await getDoc(doc(db, 'usuarios', uid));
      if (!uSnap.exists()) return null;
      return filaCuentaDesdeUsuarioDoc(uSnap);
    },

    async obtenerPublicidadHome() {
      const { db } = getFirebase();
      const ref = doc(db, 'publicidad_home', PUBLICIDAD_HOME_DOC);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    },

    /**
     * Resuelve un input del admin (UID largo o código vecino `user_tag` 4 chars) a un UID válido.
     * Reemplaza al viejo flujo por DNI/vitrina.
     */
    async resolverUidParaPublicidad(rawInput) {
      const s = String(rawInput || '').trim();
      if (!s) return { ok: false, error: 'Ingresá un UID o código vecino.' };
      if (RX_UID.test(s)) {
        const { db } = getFirebase();
        const snap = await getDoc(doc(db, 'usuarios', s));
        if (!snap.exists()) return { ok: false, error: 'No existe ese usuario.' };
        const u = snap.data() || {};
        return {
          ok: true,
          uid: s,
          nombre_publico: String(u.nombre || u.nombre_publico || '').trim() || 'Vendedor',
        };
      }
      const tag = s
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .replace(/[01IO]/g, '');
      if (tag.length !== 4) {
        return {
          ok: false,
          error: 'Ingresá un UID válido o un código vecino de 4 caracteres.',
        };
      }
      const { db } = getFirebase();
      const qs = await getDocs(
        query(collection(db, 'usuarios'), where('user_tag', '==', tag), limit(2))
      );
      if (qs.empty) return { ok: false, error: 'No encontramos ese código vecino.' };
      if (qs.size > 1) return { ok: false, error: 'Código ambiguo. Pedile el UID al usuario.' };
      const d = qs.docs[0];
      const u = d.data() || {};
      return {
        ok: true,
        uid: d.id,
        nombre_publico: String(u.nombre || u.nombre_publico || '').trim() || 'Vendedor',
      };
    },

    async guardarPublicidadHome(patch) {
      await refreshAdminAuthToken();
      const { db } = getFirebase();
      const ref = doc(db, 'publicidad_home', PUBLICIDAD_HOME_DOC);
      await setDoc(
        ref,
        { ...patch, actualizadoEnAdmin: serverTimestamp() },
        { merge: true }
      );
    },

    async obtenerProductoPublicadoResumen(productId) {
      const { db } = getFirebase();
      const s = await getDoc(doc(db, 'productos', String(productId)));
      if (!s.exists()) return null;
      const d = s.data() || {};
      if (String(d.estado || '') !== 'publicado') return null;
      return { id: s.id, titulo: d.titulo || s.id };
    },

    /** Datos para tarjeta destacado: producto publicado + UID del vendedor en `usuarios`. */
    async obtenerProductoParaDestacadoPublicidad(productId) {
      const { db } = getFirebase();
      const pid = String(productId || '').trim();
      if (!pid) return { ok: false, error: 'ID de producto vacío.' };
      const ps = await getDoc(doc(db, 'productos', pid));
      if (!ps.exists()) return { ok: false, error: 'No existe ese producto.' };
      const pd = ps.data() || {};
      if (String(pd.estado || '') !== 'publicado') {
        return { ok: false, error: 'El producto no está publicado.' };
      }
      const uid = String(pd.vendedor_uid || '').trim();
      if (!RX_UID.test(uid)) {
        return { ok: false, error: 'El producto no tiene vendedor válido.' };
      }
      const titulo = String(pd.titulo || '').trim() || 'Producto';
      const fotos = Array.isArray(pd.fotos) ? pd.fotos : [];
      const imagen_url = String(fotos[0] || '').trim() || null;
      const precio = Number(pd.precio);
      const subtitulo = Number.isFinite(precio)
        ? `$ ${precio.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`
        : null;
      return {
        ok: true,
        producto_id: ps.id,
        titulo,
        subtitulo,
        imagen_url,
        vendedor_uid: uid,
      };
    },

    async asignarTipoPremiumCuenta({ personaId, uid, tipo }) {
      const { db } = getFirebase();
      const target = String(uid || personaId || '').trim();
      if (!RX_UID.test(target)) throw new Error('UID inválido.');

      const t = String(tipo || 'free');
      const hastaMs =
        Date.now() + Math.max(1, PREMIUM_VIGENCIA_DIAS_DEFAULT) * 24 * 60 * 60 * 1000;
      let planPatch;
      if (t === 'marketPremium') {
        planPatch = {
          plan: PLAN_PREMIUM,
          negocioPremium: true,
          premium_hasta: Timestamp.fromMillis(hastaMs),
        };
      } else if (t === 'userPremium') {
        planPatch = {
          plan: PLAN_PREMIUM,
          negocioPremium: false,
          premium_hasta: Timestamp.fromMillis(hastaMs),
        };
      } else {
        planPatch = {
          plan: PLAN_FREE,
          negocioPremium: false,
          premium_hasta: deleteField(),
        };
      }

      await setDoc(
        doc(db, 'usuarios', target),
        { ...planPatch, actualizadoEn: serverTimestamp() },
        { merge: true }
      );
    },

    /**
     * Suspende o habilita el acceso a la app para el usuario.
     * Mantiene el nombre legacy `setEstadoCuentaPersona` para compat con el presenter.
     */
    async setEstadoCuentaPersona({ personaId, uid, estado }) {
      const e = String(estado || '').trim();
      if (e !== 'activa' && e !== 'suspendida') {
        throw new Error('Estado inválido (usá activa o suspendida).');
      }
      const target = String(uid || personaId || '').trim();
      if (!RX_UID.test(target)) throw new Error('UID inválido.');
      const { db } = getFirebase();
      await setDoc(
        doc(db, 'usuarios', target),
        {
          estadoCuenta: e,
          actualizadoEn: serverTimestamp(),
          ...(e === 'activa'
            ? { suspension_motivo: deleteField() }
            : { suspension_motivo: 'moderacion_admin' }),
        },
        { merge: true }
      );
    },

    async listarAdminInboxModeracion(limitN = 40) {
      await refreshAdminAuthToken();
      const { db } = getFirebase();
      const qy = query(
        collection(db, 'admin_inbox_moderacion'),
        orderBy('creadoEn', 'desc'),
        limit(Math.min(80, Math.max(1, Number(limitN) || 40)))
      );
      const snap = await getDocs(qy);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },

    async aplicarAbonoManualComision({ personaId, principalUid, uid, montoArs }) {
      const { db } = getFirebase();
      const target = String(uid || principalUid || personaId || '').trim();
      if (!RX_UID.test(target)) throw new Error('UID inválido.');
      const monto = Number(montoArs);
      if (!Number.isFinite(monto) || monto <= 0) throw new Error('Monto inválido.');

      let resumen = null;
      await runTransaction(db, async (tx) => {
        const uref = doc(db, 'usuarios', target);
        const snap = await tx.get(uref);
        if (!snap.exists()) throw new Error('Usuario no encontrado.');
        const deuda = Number(snap.data()?.comision_deuda_ars) || 0;
        const saldoFavor = Number(snap.data()?.saldo_favor_ars) || 0;
        const netOwe = Math.round(Math.max(0, deuda - saldoFavor) * 100) / 100;
        const aNet = Math.round(Math.min(monto, netOwe) * 100) / 100;
        const obligationAfter = Math.round((netOwe - monto) * 100) / 100;
        const nuevaDeuda = obligationAfter > 0 ? obligationAfter : 0;
        const nuevoSaldo = obligationAfter < 0 ? Math.round(-obligationAfter * 100) / 100 : 0;
        const excedente = Math.round(Math.max(0, monto - aNet) * 100) / 100;
        const patch = {
          comision_deuda_ars: nuevaDeuda,
          saldo_favor_ars: nuevoSaldo,
          actualizadoEn: serverTimestamp(),
        };
        if (nuevaDeuda === 0) {
          patch.comision_ventas_impagas_count = 0;
        }
        tx.update(uref, patch);
        resumen = { monto, aDeuda: aNet, excedente, nuevaDeuda, nuevoSaldo };
      });
      return resumen;
    },

    /** Ajuste directo de deuda y saldo a favor. */
    async aplicarAjusteManualComisionSaldo({
      personaId,
      uid,
      comision_deuda_ars,
      saldo_favor_ars,
    }) {
      const { db } = getFirebase();
      const target = String(uid || personaId || '').trim();
      if (!RX_UID.test(target)) throw new Error('UID inválido.');
      const deuda = Math.max(0, Math.round(Number(comision_deuda_ars) * 100) / 100);
      const saldo = Math.max(0, Math.round(Number(saldo_favor_ars) * 100) / 100);
      if (!Number.isFinite(deuda) || !Number.isFinite(saldo)) throw new Error('Montos inválidos.');

      const patch = {
        comision_deuda_ars: deuda,
        saldo_favor_ars: saldo,
        actualizadoEn: serverTimestamp(),
      };
      if (deuda === 0) {
        patch.comision_ventas_impagas_count = 0;
      }
      const batch = writeBatch(db);
      batch.set(doc(db, 'usuarios', target), patch, { merge: true });
      await batch.commit();
    },

    async obtenerProfesionalServicios(uid) {
      const { db } = getFirebase();
      const u = String(uid || '').trim();
      if (!u) return null;
      const s = await getDoc(doc(db, COL_PROFESIONALES_SERVICIOS, u));
      if (!s.exists()) return null;
      return { id: s.id, ...s.data() };
    },

    async guardarProfesionalServiciosAdmin(uid, { perfil_validado, tarjetas_saldo } = {}) {
      const { db } = getFirebase();
      const u = String(uid || '').trim();
      if (!u) throw new Error('UID vacío.');
      const ref = doc(db, COL_PROFESIONALES_SERVICIOS, u);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error('No hay perfil profesional para este usuario.');
      /** @type {Record<string, unknown>} */
      const patch = { actualizadoEn: serverTimestamp() };
      if (perfil_validado !== undefined) patch.perfil_validado = perfil_validado === true;
      if (tarjetas_saldo !== undefined) {
        const n = Math.floor(Number(tarjetas_saldo));
        const clamped = Number.isFinite(n) ? Math.min(99, Math.max(0, n)) : 0;
        patch.tarjetas_saldo = clamped;
      }
      await setDoc(ref, patch, { merge: true });
    },

    async listarTarjetasServiciosAdmin(uid) {
      const { db } = getFirebase();
      const u = String(uid || '').trim();
      if (!u) return [];
      const base = collection(db, COL_PROFESIONALES_SERVICIOS, u, SUB_TARJETAS);
      const qy = query(base, orderBy('fecha_publicacion', 'desc'), limit(40));
      const snap = await getDocs(qy);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    },

    async actualizarTarjetaServiciosAdmin(uid, tid, patch) {
      const { db } = getFirebase();
      const u = String(uid || '').trim();
      const t = String(tid || '').trim();
      if (!u || !t) throw new Error('Datos incompletos.');
      const ref = doc(db, COL_PROFESIONALES_SERVICIOS, u, SUB_TARJETAS, t);
      const allowed = [
        'titulo_caso',
        'cliente_display',
        'duracion_txt',
        'visible',
        'oculta_por_admin',
      ];
      /** @type {Record<string, unknown>} */
      const clean = {};
      for (const k of allowed) {
        if (k in patch) clean[k] = patch[k];
      }
      if (!Object.keys(clean).length) return;
      await setDoc(ref, clean, { merge: true });
    },

    async eliminarPublicacionModeracion(productoId, motivo) {
      const { fns } = getFirebase();
      if (!fns) throw new Error('Functions no disponible.');
      const pid = String(productoId || '').trim();
      const m = String(motivo || '').trim();
      if (!pid) throw new Error('Falta la publicación.');
      if (!m) throw new Error('Indicá el motivo para el vendedor.');
      const call = httpsCallable(fns, 'adminEliminarPublicacion');
      const res = await call({ productoId: pid, motivo: m });
      return res?.data;
    },
  };
}
